// src/components/Products.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";

/* ---------------- PRODUCT DATA ---------------- */

const MODELS = [
  {
    name: "Zen-Mini",
    images: Array.from({ length: 10 }, (_, i) => `/images/products/zen-mini/${i + 1}.jpg`),
    mrp: 9999,
    offer: 4999,
    prebook: 999,
    popular: false,
    cadr: 30,
    coverage: "Up to 100-150 sq ft",
    recommended: {
      use: "Study room · Kids room · Desk-side use",
      size: "10 ft × 12 ft",
      time: "~15 minutes",
    },
    features: [
      "USB Powered",
      "H13 HEPA Filter",
      "Ultra-Low Power (7W)",
      "Ideal for desk / child room",
    ],
  },
  {
    name: "Zen-S",
    images: Array.from({ length: 7 }, (_, i) => `/images/products/zen-s/${i + 1}.jpg`),
    mrp: 15999,
    offer: 7999,
    prebook: 1499,
    popular: false,
    cadr: 260,
    coverage: "Up to 250-400 sq.ft",
    recommended: {
      use: "Standard bedroom · Home office",
      size: "12 ft × 14 ft",
      time: "~12 minutes",
    },
    features: [
      "H13 HEPA Filter",
      "Low Noise Operation",
      "PM2.5 Sensor (optional)",
      "Best for bedrooms",
    ],
  },
  {
    name: "Zen-S Pro",
    images: Array.from({ length: 8 }, (_, i) => `/images/products/zen-s-pro/${i + 1}.jpg`),
    mrp: 19999,
    offer: 9999,
    prebook: 1999,
    popular: false,
    cadr: 260,
    coverage: "Up to 250-400 sq.ft",
    recommended: {
      use: "Large bedroom · Master bedroom",
      size: "12 ft × 14 ft",
      time: "~12 minutes",
    },
    features: [
      "Wi-Fi Enabled",
      "PM2.5 Display",
      "Advanced Filtration",
      "Child Lock",
    ],
  },
  {
    name: "Zen-M",
    images: Array.from({ length: 3 }, (_, i) => `/images/products/zen-m/${i + 1}.jpg`),
    mrp: 22999,
    offer: 11499,
    prebook: 1999,
    popular: true,
    cadr: 400,
    coverage: "Up to 400-600 sq.ft",
    recommended: {
      use: "Living room · Large bedroom",
      size: "16 ft × 18 ft",
      time: "~10 minutes",
    },
    features: [
      "Smart Auto Mode",
      "H13 HEPA Filter",
      "App Ready",
      "Ideal for living rooms",
    ],
  },
  {
    name: "Zen-M Pro",
    images: Array.from({ length: 4 }, (_, i) => `/images/products/zen-m-pro/${i + 1}.jpg`),
    mrp: 26999,
    offer: 13499,
    prebook: 1999,
    popular: false,
    cadr: 400,
    coverage: "Up to 400-600 sq.ft",
    recommended: {
      use: "Large living room · Premium homes",
      size: "16 ft × 18 ft",
      time: "~10 minutes",
    },
    features: [
      "Wi-Fi + App Control",
      "UVC / Ionizer Option",
      "Premium Finish",
    ],
  },
  {
    name: "Zen-L",
    images: ["/images/products/zen-l/1.jpg"],
    mrp: 31999,
    offer: 15999,
    prebook: 2999,
    popular: false,
    cadr: 500,
    coverage: "Up to 500-750 sq.ft",
    recommended: {
      use: "Large halls · Open layouts",
      size: "22 ft × 24 ft",
      time: "~7 minutes",
    },
    features: [
      "High Airflow",
      "Multi-Room Coverage",
      "H13 HEPA Filter",
    ],
  },
  {
    name: "Zen-L Pro",
    images: ["/images/products/zen-l-pro/1.jpg"],
    mrp: 70999,
    offer: 35499,
    prebook: 7999,
    popular: false,
    cadr: 700,
    coverage: "Up to 700-1,000+ sq.ft",
    recommended: {
      use: "Villas · Clinics · Offices",
      size: "25 ft × 30 ft",
      time: "~5 minutes",
    },
    features: [
      "Industrial Grade Build",
      "Stainless Steel Body",
      "Wi-Fi Enabled",
    ],
  },
];

/* ---------------- IMAGE SLIDER ---------------- */

function ImageSlider({ images }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIndex(i => (i + 1) % images.length), 3000);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <div className="relative h-56 flex items-center justify-center">
      <img src={images[index]} className="h-full object-contain" alt="" />
      {images.length > 1 && (
        <>
          <button
            onClick={() => setIndex(i => (i - 1 + images.length) % images.length)}
            className="absolute left-2 bg-white/80 rounded-full p-1 shadow"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => setIndex(i => (i + 1) % images.length)}
            className="absolute right-2 bg-white/80 rounded-full p-1 shadow"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}
    </div>
  );
}

/* ---------------- PRODUCTS ---------------- */

export const Products = () => {
  const navigate = useNavigate();

  const prebook = (p) => {
    navigate("/prebook/checkout", {
      state: {
        model: p.name,
        amount: p.prebook,
        mrp: p.offer,
        source: "product-direct",
      },
    });
  };

  return (
    <section id="products" className="py-24 px-4 bg-[var(--aerzen-silver)]/20">
      <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-12">
        {MODELS.map((p) => (
          <Card key={p.name} className="relative rounded-2xl bg-white border">
            {p.popular && (
              <Badge className="absolute top-4 right-4 bg-blue-600 text-white">
                Most Popular
              </Badge>
            )}

            <CardContent className="p-8">
              <ImageSlider images={p.images} />

              <h3 className="text-xl font-bold text-center mt-4">{p.name}</h3>

              {/* PRICE BLOCK */}
              <div className="mt-4 text-center">
                <div className="text-sm text-gray-500 line-through">
                  MRP ₹{p.mrp.toLocaleString("en-IN")}
                </div>

                <div className="text-sm font-semibold text-green-600">
                  50% OFF
                </div>

                <div className="text-xl font-bold text-[var(--aerzen-blue)]">
                  Offer Price ₹{p.offer.toLocaleString("en-IN")}
                </div>

                <div className="text-sm text-gray-700 mt-1">
                  Pre-Book Now @ ₹{p.prebook.toLocaleString("en-IN")}
                </div>
              </div>

              {/* PERFORMANCE */}
              <div className="mt-4 bg-blue-50 rounded-lg p-4 text-sm">
                <div className="font-semibold text-blue-800 mb-2">
                  PERFORMANCE
                </div>
                <div className="flex justify-between">
                  <span>CADR</span>
                  <strong>{p.cadr} m³/hr</strong>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Coverage</span>
                  <strong>{p.coverage}</strong>
                </div>
              </div>

              {/* RECOMMENDED */}
              <div className="mt-4 bg-gray-50 rounded-lg p-4 text-sm">
                <div className="font-semibold text-gray-700 mb-2">
                  RECOMMENDED FOR
                </div>
                <div>{p.recommended.use}</div>
                <div className="flex justify-between mt-1">
                  <span>Room size example</span>
                  <strong>{p.recommended.size}</strong>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Purifies room in</span>
                  <strong>{p.recommended.time}</strong>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {p.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-blue-600" />
                    {f}
                  </div>
                ))}
              </div>

              <Button
                onClick={() => prebook(p)}
                className="w-full mt-6 py-5 bg-blue-600 hover:bg-blue-700"
              >
                Pre-Book Now
              </Button>

              <p className="text-[11px] text-gray-500 mt-3 text-center">
                Delivery starts from <strong>1 May 2026</strong>
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};
